import { Directive, Input, OnInit, HostListener, EventEmitter, Output } from '@angular/core'
import { Uploader } from './uploader'
import { UploaderComponent } from './uploader.component'
import { GrayScaleTreatment } from './treatments/grayscale'
import { WaterMarkTreatment } from './treatments/watermark'

export interface Treatment {
  options: any;
  treat (source: HTMLImageElement, context: CanvasRenderingContext2D): ImageData;
}

export type CompositeOperation = 'normal' |
  // Separable Blend Modes
  'multiply' | 'screen' | 'overlay' | 'darken' | 'lighten' | 'difference' | 'exclusion' |
  'hard-light' | 'soft-light' | 'color-burn' | 'color-dodge' |
  // Non-separable Blend Modes
  'color' | 'hue' | 'saturation' | 'luminosity' |
  // Porter Duff Compositing Operators
  'clear' | 'copy' | 'destination' | 'xor' | 'lighter' |
  'destination-atop' | 'destination-in' | 'destination-out' | 'destination-over' |
  'source-atop' | 'source-in' | 'source-out' | 'source-over';

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[treatImage]'
})
export class TreatImageDirective extends Uploader implements OnInit {
  @Input() treatImage: any[] = [];
  @Input() overrideName = '';
  @Output('imageTreated') imageTreated: EventEmitter<Uploader.FileItem> = new EventEmitter;

  private treatments = {
    gray: new GrayScaleTreatment,
    watermark: new WaterMarkTreatment
  };

  maxFiles = 1;
  type = 'image/png';

  image: HTMLImageElement;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;

  constructor(
    private element: UploaderComponent
  ) {
    super();
  }

  @HostListener('fileAdded', ['$event']) onAdded (file: Uploader.FileItem) {
    this.image.src = file.url;
  }

  ngOnInit() {
    this.image = new Image;
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');

    this.storagePath = this.element.storagePath;
    this.image.crossOrigin = 'anonymous';
    this.image.onload = (event: Event) => this.treat(event);

    this.fileUploaded.subscribe((file: Uploader.FileItem) =>
      this.imageTreated.next(file)
    );
  }

  treat(event: Event): any {
    const image = <HTMLImageElement>event.target;

    this.canvas.width = image.width;
    this.canvas.height = image.height;

    if (this.treatImage.length) {
      this.treatImage.forEach(treatment => {
        this.apply(image, treatment);
      });
    }
  }

  apply(image: HTMLImageElement, treatment: any) {
    const filter = treatment.filter ? treatment.filter : treatment;
    this.treatments[filter].options = treatment.options ? treatment.options : {};
    if (treatment.clear) {
      this.context.clearRect(0, 0, image.width, image.height);
    }

    this.context.putImageData(this.treatments[filter].treat(image, this.context), 0, 0);
    if (treatment.save) {
      this.canvas.toBlob((blob) => {
        this.uploadBlob(
          blob,
          treatment.overrideName || this.overrideName || this.image.src.replace(/.*\//, '').slice(0, 32) || 'filename',
          this.type,
          treatment.storagePath || `${this.storagePath}/${filter}`,
          { filter: filter }
        );
      }, this.type, 1);
    }
  }
}
